import React, { useEffect, useState, useRef } from "react";
import styles from "./Dashboard.module.css";
import "../../../styles/components.css";
import CircleIcon from "@mui/icons-material/Circle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CachedIcon from "@mui/icons-material/Cached";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { fetchAuthSession } from "aws-amplify/auth";
import { loadStripe } from "@stripe/stripe-js";
import { FaSpinner } from "react-icons/fa";
import { describeMeasurementTime } from "../../../utils/credits";

function Dashboard() {
  const [isHidden, setIsHidden] = useState(true);
  const [eye, setEye] = useState(false); // For visibility
  const [nameCopied, setNameCopied] = useState(false);
  const [valueCopied, setValueCopied] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);

  // Handle data storage
  const [token, setToken] = useState("Fetching...");
  const [creditUsage, setCreditUsage] = useState("Fetching...");
  const [creditsRemaining, setCreditsRemaining] = useState(null);
  const [userTier, setUserTier] = useState("Fetching...");
  const [nextRenewal, setNextRenewal] = useState("Fetching...");
  const [projectTier, setProjectTier] = useState("Fetching...");
  const [upgradeLoadingProductId, setUpgradeLoadingProductId] = useState(null);
  const PRODUCT_IDS = {
    essentials: process.env.REACT_APP_PRODUCT_ID_ESSENTIALS ?? '',
    premium:   process.env.REACT_APP_PRODUCT_ID_PREMIUM   ?? '',
  };
  const didFetchUserDetails = useRef(false);

  // Fetch user details
  useEffect(() => {
    if (didFetchUserDetails.current) return; // already ran once, stop here
    didFetchUserDetails.current = true;
    fetchUserDetails();
  }, []);

  const stripePromise = loadStripe(`${process.env.REACT_APP_STRIPE_PUBLIC_KEY}`);

  const handleManageBilling = async () => {
    const auth_session = await fetchAuthSession();
    const idToken = auth_session?.tokens?.idToken;
    const jwt = auth_session?.tokens?.accessToken;
    const email = idToken?.payload?.email;

    if (!jwt) {
      console.error('No JWT token available for billing management');
      return;
    }

    if (!email) {
      console.error('No email found in token');
      return;
    }

    const res = await fetch(
      `${process.env.REACT_APP_USAGE_API}${process.env.REACT_APP_STRIPE_CREATE_PORTAL_SESSION}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ email }),
      }
    );

    if (!res.ok) {
      console.error('Billing portal request failed:', res.status, await res.text());
      return;
    }

    const data = await res.json();
    if (data.url) {
      window.open(data.url, "_blank", "noopener,noreferrer");
    } else {
      console.error("No billing portal URL received");
    }
  };

  async function handleUpgrade(productId) {
    setUpgradeLoadingProductId(productId);
    const auth_session = await fetchAuthSession();
    const idToken = auth_session?.tokens?.idToken;
    const jwt = auth_session?.tokens?.accessToken;
    const email = idToken?.payload?.email;

    if (!jwt) {
      console.error('No JWT token available for upgrade');
      setUpgradeLoadingProductId(null);
      return;
    }

    if (!email) {
      console.error("Email not found in token.");
      setUpgradeLoadingProductId(null);
      return;
    }

    const session = await fetch(
      `${process.env.REACT_APP_USAGE_API}${process.env.REACT_APP_STRIPE_CREATE_CHECKOUT_SESSION}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ email, product_id: productId }),
      }
    );

    if (!session.ok) {
      console.error('Upgrade failed:', session.status, await session.text());
      setUpgradeLoadingProductId(null);
      return;
    }

    const { sessionId } = await session.json();
    const stripe = await stripePromise;
    stripe.redirectToCheckout({ sessionId });
    setUpgradeLoadingProductId(null); // cleanup in case of failure
  }

  async function fetchUserDetails() {
    try {
      // Get JWT token
      const session = await fetchAuthSession();
      const jwt = session?.tokens?.accessToken;

      if (!jwt) {
        console.error('No JWT token available for fetching user details');
        return;
      }

      const headers = { Authorization: `Bearer ${jwt}` };

      // Fetch data concurrently
      await Promise.all([
        fetch(
          `${process.env.REACT_APP_USAGE_API}${process.env.REACT_APP_API_KEY_ENDPOINT}`,
          { headers }
        )
          .then((res) => res.json())
          .then((data) => {
            setToken(data?.response?.resultMessage || 'Error loading token');
          }),
        fetch(
          `${process.env.REACT_APP_USAGE_API}${process.env.REACT_APP_PLAN_USAGE_ENDPOINT}`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              project_name: process.env.REACT_APP_PROJECT_NAME,
            }),
          }
        )
          .then((res) => res.json())
          .then((data) => {
            const resultMessage = data?.response?.resultMessage;

            if (resultMessage && resultMessage.tier_information) {
              setCreditUsage(
                `${Math.round(resultMessage.credits_remaining || 0)}/${Math.round(
                  resultMessage.tier_information.credits || 0
                )}`
              );
              setCreditsRemaining(Math.round(resultMessage.credits_remaining || 0));

              // Capitalizing first character
              const tierName = resultMessage.tier_information.tier_name;
              if (tierName) {
                setUserTier(
                  tierName.charAt(0).toUpperCase() + tierName.slice(1)
                );
              }

              setNextRenewal(resultMessage.next_renewal || 'N/A');
              setProjectTier(resultMessage.tier || 'N/A');
            } else {
              setCreditUsage('Error loading usage');
              setUserTier('Error');
              setNextRenewal('Error');
              setProjectTier('Error');
            }
          }),
      ]);
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  }

  async function regenAPIKey() {
    try {
      // Get JWT token
      const session = await fetchAuthSession();
      const jwt = session?.tokens?.accessToken;

      if (!jwt) {
        console.error('No JWT token available for regenerating API key');
        return;
      }

      const headers = { Authorization: `Bearer ${jwt}` };

      const response = await fetch(
        `${process.env.REACT_APP_USAGE_API}${process.env.REACT_APP_REGEN_API_KEY_ENDPOINT}`,
        { method: "POST", headers }
      );

      const data = await response.json();

      if (response.ok) {
        setToken(data?.response?.resultMessage || 'Error regenerating token');
      } else {
        console.error("Error regenerating API key:", data);
      }
    } catch (error) {
      console.error("Error regenerating API key:", error);
    }
  }

  function toggleVisibility() {
    setIsHidden(!isHidden);
  }

  function eyeClick() {
    setEye(!eye);
    toggleVisibility();
  }

  function getCreditColorClass(creditUsage) {
    if (!creditUsage || creditUsage === "Fetching...") return "";

    const parts = creditUsage.split("/");
    if (parts.length !== 2) return "";

    const [remainingStr, totalStr] = parts;
    const remaining = parseFloat(remainingStr);
    const total = parseFloat(totalStr);

    if (isNaN(remaining) || isNaN(total)) return "";

    const percentRemaining = (remaining / total) * 100;

    if (remaining < 0) return styles["credits-negative"];
    if (percentRemaining < 10) return styles["credits-low"];
    return styles["credits-ok"];
  }

  function copyContent(field, content) {
    // Check if the Clipboard API is supported
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(content)
        .then(() => {
          // Show copied indicator
          if (field === "name") {
            setNameCopied(true);
            setTimeout(() => setNameCopied(false), 1000); // Reset after 1 second
          } else if (field === "value") {
            setValueCopied(true);
            setTimeout(() => setValueCopied(false), 1000); // Reset after 1 second
          } else if (field === "token") {
            setTokenCopied(true);
            setTimeout(() => setTokenCopied(false), 1000);
          }
        })
        .catch((err) => {
          console.error("Failed to copy content: ", err);
        });
    } else {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = content;
      textArea.style.position = "fixed"; // Avoid scrolling to the end of the page
      textArea.style.top = "0";
      textArea.style.left = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand("copy");
        // Show copied indicator
        if (field === "name") {
          setNameCopied(true);
          setTimeout(() => setNameCopied(false), 4000);
        } else if (field === "value") {
          setValueCopied(true);
          setTimeout(() => setValueCopied(false), 4000);
        } else if (field === "token") {
          setTokenCopied(true);
          setTimeout(() => setTokenCopied(false), 4000);
        }
      } catch (err) {
        console.error("Fallback: Could not copy content", err);
      }

      document.body.removeChild(textArea);
    }
  }
  function getCreditTooltip(creditUsage) {
    if (!creditUsage || creditUsage === "Fetching...") return "";

    const parts = creditUsage.split("/");
    if (parts.length !== 2) return "";

    const [remainingStr, totalStr] = parts;
    const remaining = parseFloat(remainingStr);
    const total = parseFloat(totalStr);

    if (isNaN(remaining) || isNaN(total)) return "";

    const percentRemaining = (remaining / total) * 100;

    if (remaining < 0)
      return "You are being charged at overage rate per credit until the end of the billing cycle.";
    if (percentRemaining < 10)
      return "You are running low on credits, they will renew at end of billing cycle.";
    return "You are using credits within your current tier.";
  }

  return (
    <div className={styles["dashboard-container"]}>
      <div className={styles["dashboard-content"]}>
        <div className={styles["dashboard-header"]}>
          <h6>Your Account</h6>
        </div>

        <div className={styles["active-tokens"]}>
          <div className={styles["active-tokens-header"]}>
            <div className={styles["name-tablet"]}>
              <p>Active API Key</p>

              <CircleIcon className={styles["circle-icon"]} />
            </div>

            <p
              className={`${styles["credits-used"]} ${getCreditColorClass(
                creditUsage
              )}`}
            >
              <span className={styles["bold-txt"]}>Credits Available:</span>{" "}
              <span
                className={getCreditColorClass(creditUsage)}
                title={getCreditTooltip(creditUsage)}
              >
                {creditUsage}
              </span>
              {creditsRemaining !== null && (
                <span className={styles["credits-explainer"]}>
                  {" "}— {describeMeasurementTime(creditsRemaining)} of live measurement left (1 credit = 30 seconds)
                </span>
              )}
            </p>
          </div>

          <div className={styles["active-tokens-content"]}>
            <div className={styles["account-info-container"]}>
              
              {/* API Key Section */}
              <div className={styles["info-section"]}>
                <div className={styles["info-section-header"]}>
                  <span className={styles["bold-txt"]}>API Key</span>
                  <div className={styles["api-controls"]}>
                    <div className={styles["dashboard-icon-container"]}>
                      {eye ? (
                        <VisibilityIcon
                          className={styles["viz-icon"]}
                          onClick={eyeClick}
                        />
                      ) : (
                        <VisibilityOffIcon
                          className={styles["viz-icon"]}
                          onClick={eyeClick}
                        />
                      )}
                    </div>
                    <div className={styles["dashboard-icon-container"]}>
                      <CachedIcon
                        className={styles["refresh-icon"]}
                        onClick={regenAPIKey}
                      />
                    </div>
                    <div className={styles["dashboard-icon-container"]}>
                      <ContentCopyIcon
                        className={styles["copy-icon"]}
                        onClick={() => copyContent("token", token)}
                      />
                      <div
                        className={
                          tokenCopied
                            ? `${styles["copied-alert"]} ${styles["show-alert"]}`
                            : styles["copied-alert"]
                        }
                      >
                        Copied
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles["api-key-value"]}>
                  {isHidden ? "*".repeat(Math.max(20, token.length)) : token}
                </div>
              </div>

              {/* Account Details Section */}
              <div className={styles["info-section"]}>
                <div className={styles["account-details-grid"]}>
                  <div className={styles["detail-item"]}>
                    <span className={styles["detail-label"]}>Tier</span>
                    <span className={styles["detail-value"]}>{userTier}</span>
                  </div>
                  <div className={styles["detail-item"]}>
                    <span className={styles["detail-label"]}>Next Renewal</span>
                    <span className={styles["detail-value"]}>{nextRenewal}</span>
                  </div>
                </div>
                {(userTier === "Premium" || userTier === "Essentials") && (
                  <button
                    onClick={handleManageBilling}
                    className={`btn btn-secondary ${styles["manage-billing-btn"]}`}
                  >
                    Manage Billing
                  </button>
                )}
              </div>

            </div>

            {userTier === "Free" && (
              <div className={styles["plan-cards-container"]}>
                <span className={styles["bold-txt"]}>Upgrade Your Plan: </span>
                <div className={styles["plan-card-row"]}>
                  <div className={styles["plan-card"]}>
                    <h5 className={styles["bold-txt"]}>Essentials</h5>
                    <p className={styles["plan-price"]}>$20 / month</p>
                    <ul>
                      <li>100 Free API Credits</li>
                      <li>3,000 API Credits ({describeMeasurementTime(3000)} of measurement)</li>
                      <li>$0.007 per Credit After</li>
                    </ul>
                    <button
                      onClick={() => handleUpgrade(PRODUCT_IDS.premium)}
                      className={`btn btn-primary ${styles["upgrade-btn"]}`}
                      disabled={upgradeLoadingProductId !== null}
                    >
                      {upgradeLoadingProductId === PRODUCT_IDS.premium ? (
                        <span>
                          <FaSpinner className={styles["spinner"]} />{" "}
                          Redirecting...
                        </span>
                      ) : (
                        "Upgrade to Essentials"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;